import type {
  WorkspaceEdge,
  WorkspaceNode,
  WorkspaceNodePosition,
  WorkspaceNodeType,
} from "../../../types/workspace";
import {
  WORKSPACE_GRAPH_BRANCH_GAP,
  WORKSPACE_GRAPH_COLUMN_GAP,
  WORKSPACE_GRAPH_ORIGIN_X,
  WORKSPACE_GRAPH_ORIGIN_Y,
  WORKSPACE_GRAPH_REPORT_LINEAGE_GAP,
  WORKSPACE_GRAPH_ROW_GAP,
  WORKSPACE_NODE_CARD_WIDTH,
  WORKSPACE_NODE_COLLISION_GAP,
  WORKSPACE_NODE_SNAP_GRID,
  WORKSPACE_PLACEHOLDER_NODE_SIZE,
  getWorkspaceNodeDimensions,
  snapWorkspaceNodeByCenter,
} from "./workspaceGraphConstants";
import {
  computePlaceholderNodePosition,
  measureSubtreeVerticalExtent,
} from "./workspaceLayoutUtils";
import { workspaceNodeShowsForwardPlaceholder } from "./workspacePlaceholderPolicy";
import {
  shouldSkipWorkspacePositionPersist,
  workspacePersistNodeId,
} from "./workspaceOptimisticReport";
import {
  listPolicyExecutingAccounts,
  WORKSPACE_EXECUTING_ACCOUNT_NODE_HEIGHT,
} from "./workspaceExecutingAccountUtils";

/**
 * Sentinel column key for ephemeral executing-account diamonds. Lanes that both
 * park EAs must clear each other even when their entity cards nest in different
 * pipeline columns.
 */
const EXECUTING_ACCOUNT_LAYOUT_COLUMN_KEY = 1_000_000_000;

const PIPELINE_TOP_Y = WORKSPACE_GRAPH_ORIGIN_Y - 40;

const PIPELINE_TYPE_ORDER: WorkspaceNodeType[] = [
  "account",
  "strategy",
  "report",
  "simulation",
  "policy",
  "combined_policy",
  "combined_simulation",
];

/** Federated Strategy canvas: Strategy is visual root (column 0), then account branches. */
const STRATEGY_PIPELINE_TYPE_ORDER: WorkspaceNodeType[] = [
  "strategy",
  "account",
  "report",
  "simulation",
  "policy",
  "combined_policy",
  "combined_simulation",
];

/** Types that may have multiple parents — layout uses primary parent only, then recenters. */
const MULTI_PARENT_LAYOUT_TYPES = new Set<WorkspaceNodeType>(["combined_policy"]);

function buildChildrenMap(edges: WorkspaceEdge[]): Map<string, string[]> {
  const childrenByParent = new Map<string, string[]>();
  for (const edge of edges) {
    const siblings = childrenByParent.get(edge.sourceNodeId) ?? [];
    siblings.push(edge.targetNodeId);
    childrenByParent.set(edge.sourceNodeId, siblings);
  }
  return childrenByParent;
}

/** First parent wins for tree layout; all parents retained separately for recenter. */
function buildParentMap(edges: WorkspaceEdge[]): Map<string, string> {
  const parentByChild = new Map<string, string>();
  for (const edge of edges) {
    if (!parentByChild.has(edge.targetNodeId)) {
      parentByChild.set(edge.targetNodeId, edge.sourceNodeId);
    }
  }
  return parentByChild;
}

function buildParentsMap(edges: WorkspaceEdge[]): Map<string, string[]> {
  const parentsByChild = new Map<string, string[]>();
  for (const edge of edges) {
    const list = parentsByChild.get(edge.targetNodeId) ?? [];
    if (!list.includes(edge.sourceNodeId)) list.push(edge.sourceNodeId);
    parentsByChild.set(edge.targetNodeId, list);
  }
  return parentsByChild;
}

/**
 * For multi-parent targets, only attach under the primary (first) parent during
 * tidy-tree placement so the node is not laid out twice.
 */
function buildLayoutChildrenMap(
  edges: WorkspaceEdge[],
  nodeById: Map<string, WorkspaceNode>
): Map<string, string[]> {
  const parentsByChild = buildParentsMap(edges);
  const childrenByParent = new Map<string, string[]>();
  for (const edge of edges) {
    const child = nodeById.get(edge.targetNodeId);
    if (child && MULTI_PARENT_LAYOUT_TYPES.has(child.type)) {
      const primary = parentsByChild.get(edge.targetNodeId)?.[0];
      if (primary !== edge.sourceNodeId) continue;
    }
    const siblings = childrenByParent.get(edge.sourceNodeId) ?? [];
    if (!siblings.includes(edge.targetNodeId)) siblings.push(edge.targetNodeId);
    childrenByParent.set(edge.sourceNodeId, siblings);
  }
  return childrenByParent;
}

function sortSiblings(nodes: WorkspaceNode[]): WorkspaceNode[] {
  return [...nodes].sort((a, b) => {
    const typeDelta =
      PIPELINE_TYPE_ORDER.indexOf(a.type) - PIPELINE_TYPE_ORDER.indexOf(b.type);
    if (typeDelta !== 0) return typeDelta;
    const titleA = String(a.meta?.title ?? a.entityId).toLowerCase();
    const titleB = String(b.meta?.title ?? b.entityId).toLowerCase();
    if (titleA !== titleB) return titleA.localeCompare(titleB);
    return a.id.localeCompare(b.id);
  });
}

function childNodesFor(
  parentId: string,
  childrenByParent: Map<string, string[]>,
  nodeById: Map<string, WorkspaceNode>
): WorkspaceNode[] {
  const ids = childrenByParent.get(parentId) ?? [];
  const children: WorkspaceNode[] = [];
  for (const id of ids) {
    const node = nodeById.get(id);
    if (node) children.push(node);
  }
  return sortSiblings(children);
}

/** Vertical gap between account/strategy branch lanes (or row gap when compact). */
function layoutBranchGap(compact: boolean): number {
  return compact ? WORKSPACE_GRAPH_ROW_GAP : WORKSPACE_GRAPH_BRANCH_GAP;
}

/** Wide gap between account-level branches; tight gap inside a report lane. */
function gapBetweenSiblings(
  parent: WorkspaceNode,
  viewMode: "account" | "strategy" = "account",
  compact = false
): number {
  if (compact) return WORKSPACE_GRAPH_ROW_GAP;
  if (parent.type === "account") return WORKSPACE_GRAPH_BRANCH_GAP;
  // Federated Strategy view: space account branches under the strategy root.
  if (parent.type === "strategy" && viewMode === "strategy") {
    return WORKSPACE_GRAPH_BRANCH_GAP;
  }
  return WORKSPACE_GRAPH_ROW_GAP;
}

/**
 * Gap after a report lane: tight row gap for empty reports; wider lineage gap
 * once the report has sims/policies so groups read apart.
 *
 * Note: Organize always runs with `compact: true` (tight account/strategy
 * packing). Report-lineage separation must still apply in compact mode —
 * otherwise every report boundary collapses to the same gap as sims in a stack.
 */
function gapAfterReportLineage(
  reportId: string,
  childrenByParent: Map<string, string[]>,
  _compact = false
): number {
  const hasDownstream = (childrenByParent.get(reportId) ?? []).length > 0;
  if (!hasDownstream) return WORKSPACE_GRAPH_ROW_GAP;
  void _compact;
  return WORKSPACE_GRAPH_REPORT_LINEAGE_GAP;
}

/**
 * Band top for the next sibling report under a strategy (or account).
 * Clears the previous report's full sim/policy stack so lineages from different
 * reports do not nest.
 */
function nextReportLineageBandTop(
  placedReportIds: string[],
  fallbackTop: number,
  positions: Map<string, WorkspaceNodePosition>,
  childrenByParent: Map<string, string[]>,
  nodeById: Map<string, WorkspaceNode>,
  edges: WorkspaceEdge[],
  compact = false
): number {
  if (placedReportIds.length === 0) return fallbackTop;
  const allNodes = Array.from(nodeById.values());
  let top = fallbackTop;
  for (const prevId of placedReportIds) {
    const prev = nodeById.get(prevId);
    const prevPos = positions.get(prevId);
    if (!prev || !prevPos) continue;
    const { height } = getWorkspaceNodeDimensions(prev);
    const cardBottom = prevPos.y + height;
    const extent = measureLaneExtent(
      prevId,
      allNodes,
      positions,
      edges,
      nodeById,
      childrenByParent
    );
    const extentBottom = extent?.bottom ?? cardBottom;
    const gap = gapAfterReportLineage(prevId, childrenByParent, compact);
    top = Math.max(top, extentBottom + gap);
  }
  return top;
}

/** Choose band top for a sibling: report lineages use full-extent clearance. */
function nextSiblingBandTop(
  parent: WorkspaceNode,
  placedSiblingIds: string[],
  nextChild: WorkspaceNode,
  fallbackTop: number,
  gap: number,
  positions: Map<string, WorkspaceNodePosition>,
  childrenByParent: Map<string, string[]>,
  nodeById: Map<string, WorkspaceNode>,
  columnXs: Map<WorkspaceNodeType, number>,
  edges: WorkspaceEdge[],
  compact = false
): number {
  // Strategy→report and account→report: keep each report's sim/policy stack
  // visually separate from the next report's.
  if (
    nextChild.type === "report" &&
    (parent.type === "strategy" || parent.type === "account")
  ) {
    const placedReports = placedSiblingIds.filter(
      (id) => nodeById.get(id)?.type === "report"
    );
    const lineageTop = nextReportLineageBandTop(
      placedReports,
      fallbackTop,
      positions,
      childrenByParent,
      nodeById,
      edges,
      compact
    );
    // Also clear non-report siblings (e.g. account→strategy before a standalone report).
    if (placedSiblingIds.length === placedReports.length) {
      return lineageTop;
    }
    const columnTop = nextColumnAwareBandTop(
      placedSiblingIds,
      nextChild.id,
      fallbackTop,
      gap,
      positions,
      childrenByParent,
      nodeById,
      columnXs
    );
    return Math.max(lineageTop, columnTop);
  }
  return nextColumnAwareBandTop(
    placedSiblingIds,
    nextChild.id,
    fallbackTop,
    gap,
    positions,
    childrenByParent,
    nodeById,
    columnXs
  );
}

/** Vertical footprint of executing-account diamonds hanging off a policy/CP. */
function executingAccountStackHeight(node: WorkspaceNode): number {
  const count = listPolicyExecutingAccounts(node).length;
  if (count === 0) return 0;
  return (
    count * WORKSPACE_EXECUTING_ACCOUNT_NODE_HEIGHT +
    Math.max(0, count - 1) * WORKSPACE_GRAPH_ROW_GAP
  );
}

/**
 * Band top for a combined_policy child. When both parent and CP have executing
 * accounts, offset the CP so the shared EA column can hold both diamonds.
 */
function combinedPolicyBandTop(
  parent: WorkspaceNode,
  parentY: number,
  child: WorkspaceNode,
  gap: number
): number {
  if (executingAccountStackHeight(parent) <= 0 || executingAccountStackHeight(child) <= 0) {
    return parentY;
  }
  return (
    parentY +
    executingAccountStackHeight(parent) +
    Math.max(gap, WORKSPACE_NODE_COLLISION_GAP)
  );
}

/**
 * Fixed left-to-right columns by node type so mixed account children
 * (strategy + standalone report + sim) never share one vertical corridor.
 *
 * When any policy→simulation Edit & run fork exists, reserve a dedicated
 * corridor after the policy column so combined policies stay in their own lane.
 */
function buildPipelineColumns(
  nodes: WorkspaceNode[],
  edges: WorkspaceEdge[] = [],
  viewMode: "account" | "strategy" = "account"
): {
  columnXs: Map<WorkspaceNodeType, number>;
  policyForkSimColumnX: number | null;
} {
  const present = new Set(nodes.map((node) => node.type));
  const columnXs = new Map<WorkspaceNodeType, number>();
  let x = WORKSPACE_GRAPH_ORIGIN_X;
  let policyForkSimColumnX: number | null = null;
  const order =
    viewMode === "strategy" ? STRATEGY_PIPELINE_TYPE_ORDER : PIPELINE_TYPE_ORDER;
  const hasPolicyForkSims = graphHasPolicyForkSimulations(nodes, edges);

  for (const type of order) {
    if (!present.has(type)) continue;
    columnXs.set(type, x);
    const sample = nodes.find((node) => node.type === type)!;
    const { width } = getWorkspaceNodeDimensions(sample);
    x += width + WORKSPACE_GRAPH_COLUMN_GAP;

    if (type === "policy" && hasPolicyForkSims) {
      policyForkSimColumnX = x;
      x += WORKSPACE_NODE_CARD_WIDTH + WORKSPACE_GRAPH_COLUMN_GAP;
    }
  }

  return { columnXs, policyForkSimColumnX };
}

function graphHasPolicyForkSimulations(
  nodes: WorkspaceNode[],
  edges: WorkspaceEdge[]
): boolean {
  if (edges.length === 0) return false;
  const nodeById = new Map(nodes.map((node) => [node.id, node]));
  return edges.some((edge) => {
    const source = nodeById.get(edge.sourceNodeId);
    const target = nodeById.get(edge.targetNodeId);
    return source?.type === "policy" && target?.type === "simulation";
  });
}

/**
 * Edit & run: park simulations in the dedicated column after policy (not the
 * shared report→sim column, and not the combined_policy column).
 */
function parkPolicyForkSimulationsDownstream(
  nodes: WorkspaceNode[],
  edges: WorkspaceEdge[],
  positions: Map<string, WorkspaceNodePosition>,
  policyForkSimColumnX: number | null
): void {
  if (policyForkSimColumnX == null) return;
  const nodeById = new Map(nodes.map((node) => [node.id, node]));

  for (const edge of edges) {
    const source = nodeById.get(edge.sourceNodeId);
    const target = nodeById.get(edge.targetNodeId);
    if (!source || !target) continue;
    if (source.type !== "policy" || target.type !== "simulation") continue;
    const parentPos = positions.get(source.id);
    if (!parentPos) continue;

    const { height: parentH } = getWorkspaceNodeDimensions(source);
    const { width, height } = getWorkspaceNodeDimensions(target);
    const y = parentPos.y + parentH / 2 - height / 2;

    positions.set(
      target.id,
      snapWorkspaceNodeByCenter(
        { x: policyForkSimColumnX, y },
        { width, height },
        WORKSPACE_NODE_SNAP_GRID
      )
    );
  }
}

function laidOutWorkspaceNodes(
  nodeById: Map<string, WorkspaceNode>,
  positions: Map<string, WorkspaceNodePosition>
): WorkspaceNode[] {
  return Array.from(nodeById.values()).map((node) => {
    const position = positions.get(node.id);
    return position ? { ...node, position } : node;
  });
}

/** Extend a subtree bottom so the next sibling clears this node's add diamond / EA stack. */
function bottomWithPlaceholderReserve(
  node: WorkspaceNode,
  contentBottom: number,
  edges: WorkspaceEdge[],
  nodeById: Map<string, WorkspaceNode>,
  positions: Map<string, WorkspaceNodePosition>,
  viewMode: "account" | "strategy" = "account"
): number {
  let bottom = contentBottom;
  const pos = positions.get(node.id);
  if (pos) {
    const { height } = getWorkspaceNodeDimensions(node);
    const eaHeight = executingAccountStackHeight(node);
    if (eaHeight > 0) {
      const centerY = pos.y + height / 2;
      bottom = Math.max(bottom, centerY + eaHeight / 2);
    }
  }
  const nodes = laidOutWorkspaceNodes(nodeById, positions);
  if (!workspaceNodeShowsForwardPlaceholder(node, edges, nodes, { viewMode })) {
    return bottom;
  }
  const live = { ...node, position: positions.get(node.id) ?? node.position };
  const placeholder = computePlaceholderNodePosition(
    live,
    edges,
    WORKSPACE_PLACEHOLDER_NODE_SIZE,
    nodes
  );
  return Math.max(bottom, placeholder.y + WORKSPACE_PLACEHOLDER_NODE_SIZE);
}

/**
 * Place children in stacked bands first, then park the parent on the first
 * child’s horizontal spine (except account diamonds, which center on reports).
 * Each node sits in its pipeline type column (account → strategy → report → …).
 *
 * Combined policies stay on the same Y as their primary parent so LR edges stay
 * mostly horizontal; additional CPs stack below in that column.
 */
function placeSubtree(
  node: WorkspaceNode,
  bandTop: number,
  columnXs: Map<WorkspaceNodeType, number>,
  childrenByParent: Map<string, string[]>,
  nodeById: Map<string, WorkspaceNode>,
  positions: Map<string, WorkspaceNodePosition>,
  edges: WorkspaceEdge[],
  viewMode: "account" | "strategy" = "account",
  compact = false
): number {
  const { width, height } = getWorkspaceNodeDimensions(node);
  const x = columnXs.get(node.type) ?? WORKSPACE_GRAPH_ORIGIN_X;
  const children = childNodesFor(node.id, childrenByParent, nodeById);

  const snapNode = (position: WorkspaceNodePosition): WorkspaceNodePosition =>
    snapWorkspaceNodeByCenter(position, { width, height }, WORKSPACE_NODE_SNAP_GRID);

  if (children.length === 0) {
    const snapped = snapNode({ x, y: bandTop });
    positions.set(node.id, snapped);
    return bottomWithPlaceholderReserve(
      node,
      snapped.y + height,
      edges,
      nodeById,
      positions,
      viewMode
    );
  }

  const gap = gapBetweenSiblings(node, viewMode, compact);
  const sameTypeChildren = children.filter((child) => child.type === node.type);
  const crossTypeChildren = children.filter((child) => child.type !== node.type);
  const combinedPolicyChildren = crossTypeChildren.filter(
    (child) => child.type === "combined_policy"
  );
  const otherCrossTypeChildren = crossTypeChildren.filter(
    (child) => child.type !== "combined_policy"
  );

  // Same-column children (simulation / combined_simulation reruns) stack under the
  // parent instead of centering the parent among them (which overlaps).
  if (sameTypeChildren.length > 0) {
    const parentSnapped = snapNode({ x, y: bandTop });
    positions.set(node.id, parentSnapped);

    let stackBottom = parentSnapped.y + height;
    let rerunCursor = stackBottom + gap;
    for (const child of sameTypeChildren) {
      const childBottom = placeSubtree(
        child,
        rerunCursor,
        columnXs,
        childrenByParent,
        nodeById,
        positions,
        edges,
        viewMode,
        compact
      );
      stackBottom = Math.max(stackBottom, childBottom);
      rerunCursor = childBottom + gap;
    }

    let crossBottom = parentSnapped.y + height;
    // Non-combined cross-type children: nest in Y when they don't share columns
    // (second sim beside first sim’s policy stack, etc.).
    const placedCrossIds: string[] = [];
    let crossCursor = parentSnapped.y;
    for (const child of otherCrossTypeChildren) {
      crossCursor = nextSiblingBandTop(
        node,
        placedCrossIds,
        child,
        parentSnapped.y,
        gap,
        positions,
        childrenByParent,
        nodeById,
        columnXs,
        edges,
        compact
      );
      const childBottom = placeSubtree(
        child,
        crossCursor,
        columnXs,
        childrenByParent,
        nodeById,
        positions,
        edges,
        viewMode,
        compact
      );
      crossBottom = Math.max(crossBottom, childBottom);
      placedCrossIds.push(child.id);
    }
    // Combined policies: next column, same spine as parent (stack extras below).
    // When both parent and CP have executing accounts, offset so EA diamonds clear.
    let combinedCursor = parentSnapped.y;
    for (const child of combinedPolicyChildren) {
      const bandTop = combinedPolicyBandTop(node, parentSnapped.y, child, gap);
      combinedCursor = Math.max(combinedCursor, bandTop);
      const childBottom = placeSubtree(
        child,
        combinedCursor,
        columnXs,
        childrenByParent,
        nodeById,
        positions,
        edges,
        viewMode,
        compact
      );
      crossBottom = Math.max(crossBottom, childBottom);
      combinedCursor = childBottom + gap;
    }

    return bottomWithPlaceholderReserve(
      node,
      Math.max(stackBottom, crossBottom),
      edges,
      nodeById,
      positions,
      viewMode
    );
  }

  // Policy → combined_policy: spine-align in the next column (extras stack below).
  // Dual executing accounts force a vertical offset so both diamonds fit.
  if (combinedPolicyChildren.length > 0 && otherCrossTypeChildren.length === 0) {
    const parentSnapped = snapNode({ x, y: bandTop });
    positions.set(node.id, parentSnapped);

    let stackBottom = parentSnapped.y + height;
    let combinedCursor = parentSnapped.y;
    for (const child of combinedPolicyChildren) {
      const bandTopForChild = combinedPolicyBandTop(node, parentSnapped.y, child, gap);
      combinedCursor = Math.max(combinedCursor, bandTopForChild);
      const childBottom = placeSubtree(
        child,
        combinedCursor,
        columnXs,
        childrenByParent,
        nodeById,
        positions,
        edges,
        viewMode,
        compact
      );
      stackBottom = Math.max(stackBottom, childBottom);
      combinedCursor = childBottom + gap;
    }

    return bottomWithPlaceholderReserve(
      node,
      stackBottom,
      edges,
      nodeById,
      positions,
      viewMode
    );
  }

  const placedChildIds: string[] = [];
  let childrenBottom = bandTop;

  for (const child of otherCrossTypeChildren) {
    const childBandTop = nextSiblingBandTop(
      node,
      placedChildIds,
      child,
      bandTop,
      gap,
      positions,
      childrenByParent,
      nodeById,
      columnXs,
      edges,
      compact
    );
    childrenBottom = Math.max(
      childrenBottom,
      placeSubtree(
        child,
        childBandTop,
        columnXs,
        childrenByParent,
        nodeById,
        positions,
        edges,
        viewMode,
        compact
      )
    );
    placedChildIds.push(child.id);
  }

  let parentY: number;
  if (node.type === "account") {
    const alignChildren =
      otherCrossTypeChildren.filter((child) => child.type === "report").length > 0
        ? otherCrossTypeChildren.filter((child) => child.type === "report")
        : otherCrossTypeChildren.length > 0
          ? otherCrossTypeChildren
          : children;
    let sumCenter = 0;
    let count = 0;
    for (const child of alignChildren) {
      const childPos = positions.get(child.id);
      if (!childPos) continue;
      const { height: childHeight } = getWorkspaceNodeDimensions(child);
      sumCenter += childPos.y + childHeight / 2;
      count += 1;
    }
    const targetCenter = count > 0 ? sumCenter / count : bandTop + height / 2;
    parentY = targetCenter - height / 2;
  } else if (otherCrossTypeChildren.length > 0) {
    // Top-align with the first child so the primary edge stays horizontal instead
    // of fanning diagonally from the mid-point of a tall child block.
    const firstChild = otherCrossTypeChildren[0]!;
    const childPos = positions.get(firstChild.id);
    parentY = childPos?.y ?? bandTop;
  } else {
    parentY = bandTop;
  }

  const snapped = snapNode({ x, y: parentY });
  positions.set(node.id, snapped);

  let stackBottom = Math.max(snapped.y + height, childrenBottom);
  if (combinedPolicyChildren.length > 0) {
    let combinedCursor = snapped.y;
    for (const child of combinedPolicyChildren) {
      const bandTopForChild = combinedPolicyBandTop(node, snapped.y, child, gap);
      combinedCursor = Math.max(combinedCursor, bandTopForChild);
      const childBottom = placeSubtree(
        child,
        combinedCursor,
        columnXs,
        childrenByParent,
        nodeById,
        positions,
        edges,
        viewMode,
        compact
      );
      stackBottom = Math.max(stackBottom, childBottom);
      combinedCursor = childBottom + gap;
    }
  }

  return bottomWithPlaceholderReserve(
    node,
    stackBottom,
    edges,
    nodeById,
    positions,
    viewMode
  );
}

function layoutNodesWithoutEdges(
  nodes: WorkspaceNode[],
  viewMode: "account" | "strategy" = "account"
): Map<string, WorkspaceNodePosition> {
  const { columnXs } = buildPipelineColumns(nodes, [], viewMode);
  const positions = new Map<string, WorkspaceNodePosition>();

  // Stack by type within each pipeline column.
  const byType = new Map<WorkspaceNodeType, WorkspaceNode[]>();
  for (const node of sortSiblings(nodes)) {
    const list = byType.get(node.type) ?? [];
    list.push(node);
    byType.set(node.type, list);
  }

  Array.from(byType.entries()).forEach(([type, group]) => {
    const x = columnXs.get(type) ?? WORKSPACE_GRAPH_ORIGIN_X;
    let y = PIPELINE_TOP_Y;
    for (const node of group) {
      const { width, height } = getWorkspaceNodeDimensions(node);
      positions.set(
        node.id,
        snapWorkspaceNodeByCenter({ x, y }, { width, height }, WORKSPACE_NODE_SNAP_GRID)
      );
      y += height + WORKSPACE_GRAPH_ROW_GAP;
    }
  });

  return positions;
}

/**
 * Left-to-right tidy tree:
 * - each type owns a fixed pipeline column
 * - siblings stack without overlapping in shared columns (other columns may nest in Y)
 * - parents top-align to their first child (account diamonds still center on reports)
 * - combined policies share the parent spine in the next column
 * - account-level branches get a modest branch gap; Organize packs empty corridors
 */
export function layoutWorkspaceGraphTree(
  nodes: WorkspaceNode[],
  edges: WorkspaceEdge[],
  options?: {
    viewMode?: "account" | "strategy";
    compact?: boolean;
    /** Accepted for API parity with billing-aware callers; placeholders stay hidden. */
    maxSimulationsPerReport?: number;
  }
): Map<string, WorkspaceNodePosition> {
  const viewMode = options?.viewMode ?? "account";
  const compact = options?.compact ?? false;
  if (nodes.length === 0) return new Map();
  if (edges.length === 0) {
    return layoutNodesWithoutEdges(nodes, viewMode);
  }

  const nodeById = new Map(nodes.map((node) => [node.id, node]));
  const childrenByParent = buildLayoutChildrenMap(edges, nodeById);
  const parentByChild = buildParentMap(edges);
  const parentsByChild = buildParentsMap(edges);
  const { columnXs, policyForkSimColumnX } = buildPipelineColumns(
    nodes,
    edges,
    viewMode
  );
  const positions = new Map<string, WorkspaceNodePosition>();
  const branchGap = layoutBranchGap(compact);

  const roots = sortSiblings(
    nodes.filter((node) => {
      const parentId = parentByChild.get(node.id);
      if (!parentId) return true;
      // Account canvas safety: promote account if parented to something other than strategy.
      // Federated Strategy view: accounts hang under strategy-root — keep them as children.
      if (node.type === "account") {
        const parent = nodeById.get(parentId);
        return parent?.type !== "strategy";
      }
      return false;
    })
  );

  if (roots.length === 0) {
    return layoutNodesWithoutEdges(nodes, viewMode);
  }

  let yCursor = PIPELINE_TOP_Y;
  for (let index = 0; index < roots.length; index += 1) {
    const root = roots[index]!;
    const bottom = placeSubtree(
      root,
      yCursor,
      columnXs,
      childrenByParent,
      nodeById,
      positions,
      edges,
      viewMode,
      compact
    );
    yCursor = bottom + branchGap;
  }

  const unresolved = nodes.filter((node) => !positions.has(node.id));
  if (unresolved.length > 0) {
    let maxBottom = PIPELINE_TOP_Y;
    Array.from(positions.entries()).forEach(([id, position]) => {
      const node = nodeById.get(id);
      if (!node) return;
      const { height } = getWorkspaceNodeDimensions(node);
      maxBottom = Math.max(maxBottom, position.y + height);
    });

    const unresolvedIds = new Set(unresolved.map((node) => node.id));
    const orphanRoots = sortSiblings(
      unresolved.filter((node) => {
        const parentId = parentByChild.get(node.id);
        return !parentId || !unresolvedIds.has(parentId);
      })
    );

    let orphanCursor = maxBottom + branchGap;
    for (const root of orphanRoots) {
      const bottom = placeSubtree(
        root,
        orphanCursor,
        columnXs,
        childrenByParent,
        nodeById,
        positions,
        edges,
        viewMode,
        compact
      );
      orphanCursor = bottom + branchGap;
    }

    for (const node of unresolved) {
      if (positions.has(node.id)) continue;
      const { width, height } = getWorkspaceNodeDimensions(node);
      const x = columnXs.get(node.type) ?? WORKSPACE_GRAPH_ORIGIN_X;
      positions.set(
        node.id,
        snapWorkspaceNodeByCenter(
          { x, y: orphanCursor },
          { width, height },
          WORKSPACE_NODE_SNAP_GRID
        )
      );
      orphanCursor += height + WORKSPACE_GRAPH_ROW_GAP;
    }
  }

  parkPolicyForkSimulationsDownstream(
    nodes,
    edges,
    positions,
    policyForkSimColumnX
  );

  // Multi-parent nodes (combined_policy) were placed under the primary parent
  // only. Keep them on the primary parent’s horizontal spine, then shift
  // descendants by the same ΔY so combined_simulation stays aligned.
  //
  // Skip when parents live in different Strategy account lanes OR different
  // account→strategy/report sibling branches: mean-Y would park the combined
  // card in the empty corridor between stacks, inflate the primary branch’s
  // measured height, and leave huge vertical holes after overlap resolution.
  for (const node of nodes) {
    if (!MULTI_PARENT_LAYOUT_TYPES.has(node.type)) continue;
    const parentIds = parentsByChild.get(node.id) ?? [];
    if (parentIds.length < 2) continue;
    if (
      multiParentSpansDistinctAccountLanes(parentIds, parentByChild, nodeById) ||
      multiParentSpansDistinctAccountChildBranches(parentIds, parentByChild, nodeById)
    ) {
      continue;
    }
    const oldPos = positions.get(node.id);
    if (!oldPos) continue;

    const primaryId = parentIds[0]!;
    const primary = nodeById.get(primaryId);
    const primaryPos = positions.get(primaryId);
    if (!primary || !primaryPos) continue;
    // Dual executing accounts need separate spines — keep the offset from placeSubtree.
    if (
      executingAccountStackHeight(primary) > 0 &&
      executingAccountStackHeight(node) > 0
    ) {
      continue;
    }

    const { width, height } = getWorkspaceNodeDimensions(node);
    const x = columnXs.get(node.type) ?? WORKSPACE_GRAPH_ORIGIN_X;
    const newPos = snapWorkspaceNodeByCenter(
      { x, y: primaryPos.y },
      { width, height },
      WORKSPACE_NODE_SNAP_GRID
    );
    const dy = newPos.y - oldPos.y;
    positions.set(node.id, newPos);
    if (dy !== 0) {
      shiftLayoutDescendants(node.id, dy, childrenByParent, positions);
    }
  }

  // Recenter (and any residual packing) can leave sibling lanes overlapping —
  // spread each collision bidirectionally (upper lane up, lower lane down).
  resolveSiblingLaneOverlaps(nodes, positions, childrenByParent, edges, nodeById, {
    viewMode,
    compact,
  });
  separateSameColumnOverlaps(nodes, positions, childrenByParent, edges, nodeById);
  // Absolute guarantee: no two cards may share bounds (same gap as drag collision).
  enforceNoNodeOverlaps(nodes, positions, {
    viewMode,
    childrenByParent,
    nodeById,
    parentByChild,
  });
  // Federated Strategy view: re-stack each account lane so reports never interleave
  // across accounts after global overlap nudges.
  if (viewMode === "strategy") {
    packStrategyAccountLanes(nodes, positions, childrenByParent, edges, nodeById, {
      compact,
    });
  }

  // Collapse leftover empty bands between sibling branches (account→strategy /
  // account→report, etc.) so padding matches the intended branch/row gap.
  packSiblingLanes(nodes, positions, childrenByParent, edges, nodeById, {
    viewMode,
    compact,
  });

  // Fill empty vertical holes inside a single pipeline column by pulling later
  // lanes up (whole report/strategy subtree), even when other columns still
  // occupy that Y — global band collapse cannot see those column-local gaps.
  packCrossLaneColumnGaps(
    nodes,
    positions,
    childrenByParent,
    nodeById,
    layoutBranchGap(compact)
  );

  // Last resort: pack each pipeline column against itself only. Cross-column
  // clearance above can leave sim-column holes when the report column is busy;
  // this pass closes those holes, then parents re-spine when their column allows.
  packPipelineColumnsTight(nodes, positions, childrenByParent, nodeById, {
    gap: layoutBranchGap(compact),
  });
  // Re-assert report lineage gaps after column packing (those passes must not
  // leave different reports' sim/policy stacks butted together).
  packSiblingLanes(nodes, positions, childrenByParent, edges, nodeById, {
    viewMode,
    compact,
  });
  respineParentsToChildren(nodes, positions, childrenByParent, nodeById, edges, {
    gap: layoutBranchGap(compact),
  });
  // Snap cross-column children onto their primary parent's Y when the slot is
  // free (sim→policy, policy→CP, …) so lanes read as a horizontal grid.
  alignChildrenToParentSpines(nodes, positions, childrenByParent, nodeById, edges, {
    gap: layoutBranchGap(compact),
  });
  // Push EA-bearing parents apart when shared-column diamonds would overlap.
  resolveExecutingAccountOverlaps(nodes, positions, childrenByParent, nodeById);
  enforceNoNodeOverlaps(nodes, positions, {
    viewMode,
    childrenByParent,
    nodeById,
    parentByChild,
  });

  // Re-apply after overlap/packing so fork sims stay in their dedicated column.
  parkPolicyForkSimulationsDownstream(
    nodes,
    edges,
    positions,
    policyForkSimColumnX
  );

  return positions;
}

function measureLaneExtent(
  laneRootId: string,
  nodes: WorkspaceNode[],
  positions: Map<string, WorkspaceNodePosition>,
  edges: WorkspaceEdge[],
  nodeById: Map<string, WorkspaceNode>,
  childrenByParent: Map<string, string[]>
): { top: number; bottom: number } | null {
  // Measure against live layout positions — not the pre-layout node.position values.
  const laidOut = laidOutWorkspaceNodes(nodeById, positions);
  const laidOutById = new Map(laidOut.map((node) => [node.id, node]));
  return measureSubtreeVerticalExtent(
    laneRootId,
    laidOut,
    edges,
    laidOutById,
    childrenByParent
  );
}

/** Snap X so main-sim vs fork-sim columns stay distinct even for the same type. */
function layoutColumnKey(x: number): number {
  return Math.round(x / WORKSPACE_NODE_SNAP_GRID) * WORKSPACE_NODE_SNAP_GRID;
}

/** Pipeline X keys a subtree will occupy (by node type), before final parking. */
function estimateSubtreeColumnKeys(
  rootId: string,
  childrenByParent: Map<string, string[]>,
  nodeById: Map<string, WorkspaceNode>,
  columnXs: Map<WorkspaceNodeType, number>
): number[] {
  const keys = new Set<number>();
  const stack = [rootId];
  const seen = new Set<string>();
  while (stack.length > 0) {
    const id = stack.pop()!;
    if (seen.has(id)) continue;
    seen.add(id);
    const node = nodeById.get(id);
    if (!node) continue;
    const x = columnXs.get(node.type);
    if (x != null) keys.add(layoutColumnKey(x));
    if (executingAccountStackHeight(node) > 0) {
      keys.add(EXECUTING_ACCOUNT_LAYOUT_COLUMN_KEY);
    }
    for (const childId of childrenByParent.get(id) ?? []) {
      stack.push(childId);
    }
  }
  return Array.from(keys);
}

/**
 * Y for the next sibling so it clears prior siblings only in columns it will
 * use — a second sim under a report can sit beside the first sim’s policy stack.
 */
function nextColumnAwareBandTop(
  placedSiblingIds: string[],
  nextChildId: string,
  fallbackTop: number,
  gap: number,
  positions: Map<string, WorkspaceNodePosition>,
  childrenByParent: Map<string, string[]>,
  nodeById: Map<string, WorkspaceNode>,
  columnXs: Map<WorkspaceNodeType, number>
): number {
  if (placedSiblingIds.length === 0) return fallbackTop;

  const nextCols = new Set(
    estimateSubtreeColumnKeys(nextChildId, childrenByParent, nodeById, columnXs)
  );
  let top = fallbackTop;
  let shared = false;
  for (const prevId of placedSiblingIds) {
    const prevCols = measureSubtreeColumnExtents(
      prevId,
      positions,
      childrenByParent,
      nodeById
    );
    Array.from(prevCols.entries()).forEach(([col, ext]) => {
      if (!nextCols.has(col)) return;
      shared = true;
      top = Math.max(top, ext.bottom + gap);
    });
  }
  if (shared) return top;

  for (const prevId of placedSiblingIds) {
    const pos = positions.get(prevId);
    const node = nodeById.get(prevId);
    if (!pos || !node) continue;
    const { height } = getWorkspaceNodeDimensions(node);
    top = Math.max(top, pos.y + height + gap);
  }
  return top;
}

/**
 * Per-column vertical extents for a layout subtree. Sibling lanes may share Y
 * when they occupy different pipeline columns (e.g. short report→sim beside a
 * taller branch’s policy / combined-policy stack).
 *
 * Executing-account diamonds share one rightmost column — include that sentinel
 * so two EA-bearing lanes cannot nest on top of each other.
 */
function measureSubtreeColumnExtents(
  rootId: string,
  positions: Map<string, WorkspaceNodePosition>,
  childrenByParent: Map<string, string[]>,
  nodeById: Map<string, WorkspaceNode>
): Map<number, { top: number; bottom: number }> {
  const extents = new Map<number, { top: number; bottom: number }>();
  const merge = (key: number, top: number, bottom: number) => {
    const prev = extents.get(key);
    if (!prev) {
      extents.set(key, { top, bottom });
    } else {
      extents.set(key, {
        top: Math.min(prev.top, top),
        bottom: Math.max(prev.bottom, bottom),
      });
    }
  };
  const stack = [rootId];
  const seen = new Set<string>();
  while (stack.length > 0) {
    const id = stack.pop()!;
    if (seen.has(id)) continue;
    seen.add(id);
    const node = nodeById.get(id);
    const pos = positions.get(id);
    if (!node || !pos) continue;
    const { height } = getWorkspaceNodeDimensions(node);
    const top = pos.y;
    const bottom = pos.y + height;
    merge(layoutColumnKey(pos.x), top, bottom);
    const eaHeight = executingAccountStackHeight(node);
    if (eaHeight > 0) {
      const centerY = pos.y + height / 2;
      merge(
        EXECUTING_ACCOUNT_LAYOUT_COLUMN_KEY,
        centerY - eaHeight / 2,
        centerY + eaHeight / 2
      );
    }
    for (const childId of childrenByParent.get(id) ?? []) {
      stack.push(childId);
    }
  }
  return extents;
}

/**
 * Dy to apply to `lowerRootId` so every shared column clears `gap` below the
 * upper lane. Positive = push lower down; negative = room to pull up.
 * `null` when the lanes share no columns (full Y nesting is allowed).
 */
function columnAwareLaneClearanceDy(
  upperRootId: string,
  lowerRootId: string,
  gap: number,
  positions: Map<string, WorkspaceNodePosition>,
  childrenByParent: Map<string, string[]>,
  nodeById: Map<string, WorkspaceNode>
): number | null {
  const upperCols = measureSubtreeColumnExtents(
    upperRootId,
    positions,
    childrenByParent,
    nodeById
  );
  const lowerCols = measureSubtreeColumnExtents(
    lowerRootId,
    positions,
    childrenByParent,
    nodeById
  );
  let maxNeeded = -Infinity;
  let shared = false;
  Array.from(lowerCols.entries()).forEach(([col, lowerExt]) => {
    const upperExt = upperCols.get(col);
    if (!upperExt) return;
    shared = true;
    maxNeeded = Math.max(maxNeeded, upperExt.bottom + gap - lowerExt.top);
  });
  if (!shared) return null;
  return maxNeeded;
}

/** Most restrictive clearance dy for `lower` against every earlier sibling lane. */
function columnAwareClearanceAgainstUppers(
  lowerRootId: string,
  upperRootIds: string[],
  gap: number,
  positions: Map<string, WorkspaceNodePosition>,
  childrenByParent: Map<string, string[]>,
  nodeById: Map<string, WorkspaceNode>
): number {
  let dy = -Infinity;
  let anyShared = false;
  for (const upperRootId of upperRootIds) {
    const needed = columnAwareLaneClearanceDy(
      upperRootId,
      lowerRootId,
      gap,
      positions,
      childrenByParent,
      nodeById
    );
    if (needed === null) continue;
    anyShared = true;
    dy = Math.max(dy, needed);
  }
  if (!anyShared) {
    const lowerCols = measureSubtreeColumnExtents(
      lowerRootId,
      positions,
      childrenByParent,
      nodeById
    );
    let lowerTop = Infinity;
    Array.from(lowerCols.values()).forEach((ext) => {
      lowerTop = Math.min(lowerTop, ext.top);
    });
    if (!Number.isFinite(lowerTop)) return 0;
    return PIPELINE_TOP_Y - lowerTop;
  }
  return dy;
}

/**
 * Pack each simulation pipeline column against itself only (ignore other
 * columns). Report-column compaction is intentionally skipped — pulling a
 * report+sim lane up beside a same-column rerun creates overlaps that the
 * overlap pass then splits, breaking report↔sim spines.
 */
function packPipelineColumnsTight(
  nodes: WorkspaceNode[],
  positions: Map<string, WorkspaceNodePosition>,
  childrenByParent: Map<string, string[]>,
  nodeById: Map<string, WorkspaceNode>,
  options?: { gap?: number }
): void {
  const gap = options?.gap ?? WORKSPACE_GRAPH_ROW_GAP;
  const parentByChild = buildLayoutParentMap(childrenByParent);
  const packTypes = new Set<WorkspaceNodeType>([
    "simulation",
    "combined_simulation",
  ]);

  const findReportAncestor = (nodeId: string): string | null => {
    let current = nodeId;
    for (let guard = 0; guard < 48; guard += 1) {
      const node = nodeById.get(current);
      if (node?.type === "report") return current;
      const parentId = parentByChild.get(current);
      if (!parentId) return null;
      current = parentId;
    }
    return null;
  };

  for (let pass = 0; pass < 12; pass += 1) {
    let moved = false;
    const byColumn = new Map<
      number,
      Array<{ id: string; top: number; bottom: number }>
    >();
    for (const node of nodes) {
      if (!packTypes.has(node.type)) continue;
      const pos = positions.get(node.id);
      if (!pos) continue;
      const { height } = getWorkspaceNodeDimensions(node);
      const key = layoutColumnKey(pos.x);
      const list = byColumn.get(key) ?? [];
      list.push({ id: node.id, top: pos.y, bottom: pos.y + height });
      byColumn.set(key, list);
    }

    Array.from(byColumn.values()).forEach((items) => {
      const ordered = [...items].sort((a, b) => {
        if (a.top !== b.top) return a.top - b.top;
        return a.id.localeCompare(b.id);
      });
      for (let index = 1; index < ordered.length; index += 1) {
        const upper = ordered[index - 1]!;
        const lower = ordered[index]!;
        // Re-read live positions — a prior shift in this pass may have moved them.
        const upperPos = positions.get(upper.id);
        const lowerPos = positions.get(lower.id);
        const upperNode = nodeById.get(upper.id);
        const lowerNode = nodeById.get(lower.id);
        if (!upperPos || !lowerPos || !upperNode || !lowerNode) continue;

        // Sims from different reports must keep lineage separation.
        const upperReport = findReportAncestor(upper.id);
        const lowerReport = findReportAncestor(lower.id);
        if (
          upperReport &&
          lowerReport &&
          upperReport !== lowerReport
        ) {
          continue;
        }

        const upperBottom =
          upperPos.y + getWorkspaceNodeDimensions(upperNode).height;
        const excess = lowerPos.y - upperBottom - gap;
        if (excess <= WORKSPACE_NODE_SNAP_GRID) continue;
        const dy = -excess;

        // Keep horizontal spines: if the primary parent shared this card’s Y,
        // try to pull it up by the same dy before moving the subtree.
        const parentId = parentByChild.get(lower.id);
        if (parentId) {
          const parent = nodeById.get(parentId);
          const parentPos = positions.get(parentId);
          if (
            parent &&
            parentPos &&
            parent.type !== "account" &&
            Math.abs(parentPos.y - lowerPos.y) <= WORKSPACE_NODE_SNAP_GRID
          ) {
            const { width, height } = getWorkspaceNodeDimensions(parent);
            const parentTargetY = parentPos.y + dy;
            if (
              columnSlotFree(
                parentId,
                parentPos.x,
                parentTargetY,
                width,
                height,
                gap,
                nodes,
                positions,
                nodeById
              )
            ) {
              positions.set(parentId, { x: parentPos.x, y: parentTargetY });
            }
          }
        }

        shiftLayoutSubtreeFrom(lower.id, dy, childrenByParent, positions);
        moved = true;
      }
    });
    if (!moved) break;
  }
}

/** True when `id` can sit at (x, y) without overlapping any other card in its column. */
function columnSlotFree(
  id: string,
  x: number,
  y: number,
  width: number,
  height: number,
  gap: number,
  nodes: WorkspaceNode[],
  positions: Map<string, WorkspaceNodePosition>,
  nodeById: Map<string, WorkspaceNode>
): boolean {
  const col = layoutColumnKey(x);
  const top = y;
  const bottom = y + height;
  for (const node of nodes) {
    if (node.id === id) continue;
    const pos = positions.get(node.id);
    if (!pos) continue;
    if (layoutColumnKey(pos.x) !== col) continue;
    const other = getWorkspaceNodeDimensions(node);
    const otherBottom = pos.y + other.height;
    if (top < otherBottom + gap && bottom + gap > pos.y) return false;
    // Also reject true bounding-box overlap even across slight x jitter.
    if (
      x < pos.x + other.width + gap &&
      x + width + gap > pos.x &&
      top < otherBottom + gap &&
      bottom + gap > pos.y
    ) {
      return false;
    }
  }
  return true;
}

/**
 * After column-tight packing, pull parents UP to their topmost cross-column
 * child when the parent column has room (restores report↔sim spines).
 */
function respineParentsToChildren(
  nodes: WorkspaceNode[],
  positions: Map<string, WorkspaceNodePosition>,
  childrenByParent: Map<string, string[]>,
  nodeById: Map<string, WorkspaceNode>,
  _edges: WorkspaceEdge[],
  options?: { gap?: number }
): void {
  const gap = options?.gap ?? WORKSPACE_GRAPH_ROW_GAP;

  for (let pass = 0; pass < 6; pass += 1) {
    let moved = false;
    Array.from(childrenByParent.entries()).forEach(([parentId, childIds]) => {
      const parent = nodeById.get(parentId);
      const parentPos = positions.get(parentId);
      if (!parent || !parentPos) return;
      if (parent.type === "account") return;

      const parentCol = layoutColumnKey(parentPos.x);
      let targetY = Infinity;
      for (const childId of childIds) {
        const childPos = positions.get(childId);
        if (!childPos) continue;
        if (layoutColumnKey(childPos.x) === parentCol) continue;
        targetY = Math.min(targetY, childPos.y);
      }
      if (!Number.isFinite(targetY)) return;

      const dy = targetY - parentPos.y;
      // Only pull up — never drag a parent down onto a stacked child band.
      if (dy >= -WORKSPACE_NODE_SNAP_GRID) return;

      const { width, height } = getWorkspaceNodeDimensions(parent);
      if (
        !columnSlotFree(
          parentId,
          parentPos.x,
          targetY,
          width,
          height,
          gap,
          nodes,
          positions,
          nodeById
        )
      ) {
        return;
      }

      // Move parent only — children already sit at targetY from column packing.
      positions.set(parentId, { x: parentPos.x, y: targetY });
      moved = true;
    });
    if (!moved) break;
  }
}

const SPINE_ALIGN_CHILD_TYPES = new Set<WorkspaceNodeType>([
  "simulation",
  "policy",
  "combined_policy",
  "combined_simulation",
]);

function collectLayoutSubtreeIds(
  rootId: string,
  childrenByParent: Map<string, string[]>
): Set<string> {
  const ids = new Set<string>([rootId]);
  const stack = [...(childrenByParent.get(rootId) ?? [])];
  while (stack.length > 0) {
    const id = stack.pop()!;
    if (ids.has(id)) continue;
    ids.add(id);
    for (const childId of childrenByParent.get(id) ?? []) {
      stack.push(childId);
    }
  }
  return ids;
}

/**
 * True when shifting `rootId`'s layout subtree by `dy` won't collide with any
 * non-subtree card in the same columns.
 */
function subtreeVerticalShiftClear(
  rootId: string,
  dy: number,
  gap: number,
  nodes: WorkspaceNode[],
  positions: Map<string, WorkspaceNodePosition>,
  nodeById: Map<string, WorkspaceNode>,
  childrenByParent: Map<string, string[]>
): boolean {
  if (Math.abs(dy) < 0.5) return true;
  const subtree = collectLayoutSubtreeIds(rootId, childrenByParent);
  let clear = true;
  Array.from(subtree).forEach((id) => {
    if (!clear) return;
    const node = nodeById.get(id);
    const pos = positions.get(id);
    if (!node || !pos) return;
    const { width, height } = getWorkspaceNodeDimensions(node);
    const nextY = pos.y + dy;
    const col = layoutColumnKey(pos.x);
    for (const other of nodes) {
      if (subtree.has(other.id)) continue;
      const otherPos = positions.get(other.id);
      if (!otherPos) continue;
      if (layoutColumnKey(otherPos.x) !== col) continue;
      const otherSize = getWorkspaceNodeDimensions(other);
      if (
        nextY < otherPos.y + otherSize.height + gap &&
        nextY + height + gap > otherPos.y &&
        pos.x < otherPos.x + otherSize.width + gap &&
        pos.x + width + gap > otherPos.x
      ) {
        clear = false;
        return;
      }
    }
  });
  return clear;
}

/**
 * Snap each cross-column child onto its primary parent's Y when the whole
 * subtree can move into that slot. Same-column stacks (reruns, policy forks)
 * are left alone so vertical stacking stays intact.
 */
function alignChildrenToParentSpines(
  nodes: WorkspaceNode[],
  positions: Map<string, WorkspaceNodePosition>,
  childrenByParent: Map<string, string[]>,
  nodeById: Map<string, WorkspaceNode>,
  edges: WorkspaceEdge[],
  options?: { gap?: number }
): void {
  const gap = options?.gap ?? WORKSPACE_GRAPH_ROW_GAP;
  const parentsByChild = buildParentsMap(edges);

  for (let pass = 0; pass < 8; pass += 1) {
    let moved = false;
    const candidates: Array<{ id: string; targetY: number; dy: number }> = [];

    for (const node of nodes) {
      if (!SPINE_ALIGN_CHILD_TYPES.has(node.type)) continue;
      const parentIds = parentsByChild.get(node.id) ?? [];
      const primaryId = parentIds[0];
      if (!primaryId) continue;
      const parent = nodeById.get(primaryId);
      const parentPos = positions.get(primaryId);
      const pos = positions.get(node.id);
      if (!parent || !parentPos || !pos) continue;
      if (parent.type === "account") continue;
      // Same-column child = stacked under parent (rerun / derived policy).
      if (layoutColumnKey(pos.x) === layoutColumnKey(parentPos.x)) continue;
      // Dual executing accounts need separate spines — do not re-align onto parent.
      if (
        executingAccountStackHeight(parent) > 0 &&
        executingAccountStackHeight(node) > 0
      ) {
        continue;
      }

      const dy = parentPos.y - pos.y;
      // Only climb up onto the parent spine — never drag a packed child back
      // down (e.g. sims nested above a stuck report column).
      if (dy >= -WORKSPACE_NODE_SNAP_GRID) continue;
      candidates.push({ id: node.id, targetY: parentPos.y, dy });
    }

    candidates.sort((a, b) => {
      if (a.targetY !== b.targetY) return a.targetY - b.targetY;
      return Math.abs(a.dy) - Math.abs(b.dy);
    });

    for (const candidate of candidates) {
      const pos = positions.get(candidate.id);
      const parentIds = parentsByChild.get(candidate.id) ?? [];
      const primaryId = parentIds[0];
      if (!pos || !primaryId) continue;
      const parentPos = positions.get(primaryId);
      if (!parentPos) continue;
      const dy = parentPos.y - pos.y;
      if (dy >= -WORKSPACE_NODE_SNAP_GRID) continue;
      if (
        !subtreeVerticalShiftClear(
          candidate.id,
          dy,
          gap,
          nodes,
          positions,
          nodeById,
          childrenByParent
        )
      ) {
        continue;
      }
      shiftLayoutSubtreeFrom(candidate.id, dy, childrenByParent, positions);
      moved = true;
    }
    if (!moved) break;
  }
}

/**
 * When multiple policies/combined_policies park executing accounts in the shared
 * rightmost column, push the lower parent's subtree down until diamond stacks
 * clear. Restructures real nodes so the collision trap does not fight spine sync.
 */
function resolveExecutingAccountOverlaps(
  nodes: WorkspaceNode[],
  positions: Map<string, WorkspaceNodePosition>,
  childrenByParent: Map<string, string[]>,
  nodeById: Map<string, WorkspaceNode>
): void {
  const clearGap = Math.max(WORKSPACE_GRAPH_ROW_GAP, WORKSPACE_NODE_COLLISION_GAP);

  const slotFor = (
    node: WorkspaceNode
  ): { top: number; bottom: number } | null => {
    const eaHeight = executingAccountStackHeight(node);
    if (eaHeight <= 0) return null;
    const pos = positions.get(node.id);
    if (!pos) return null;
    const { height } = getWorkspaceNodeDimensions(node);
    const centerY = pos.y + height / 2;
    return { top: centerY - eaHeight / 2, bottom: centerY + eaHeight / 2 };
  };

  for (let pass = 0; pass < 8; pass += 1) {
    const bearers = nodes
      .filter((node) => executingAccountStackHeight(node) > 0 && positions.has(node.id))
      .sort((a, b) => {
        const aSlot = slotFor(a);
        const bSlot = slotFor(b);
        const aTop = aSlot?.top ?? 0;
        const bTop = bSlot?.top ?? 0;
        if (aTop !== bTop) return aTop - bTop;
        return a.id.localeCompare(b.id);
      });

    let moved = false;
    for (let index = 1; index < bearers.length; index += 1) {
      const upper = bearers[index - 1]!;
      const lower = bearers[index]!;
      const upperSlot = slotFor(upper);
      const lowerSlot = slotFor(lower);
      if (!upperSlot || !lowerSlot) continue;
      const overlap = upperSlot.bottom + clearGap - lowerSlot.top;
      if (overlap <= 0.5) continue;
      shiftLayoutSubtreeFrom(lower.id, overlap, childrenByParent, positions);
      moved = true;
    }
    if (!moved) break;
  }
}

/**
 * Close empty vertical holes within a pipeline column by pulling a later lane
 * (report/strategy child subtree) upward. Unlike global Y-band collapse, this
 * ignores cards in other columns so sims can nest beside a taller policy stack.
 *
 * Only acts across different lane roots — gaps inside one lane stay intact.
 */
function packCrossLaneColumnGaps(
  nodes: WorkspaceNode[],
  positions: Map<string, WorkspaceNodePosition>,
  childrenByParent: Map<string, string[]>,
  nodeById: Map<string, WorkspaceNode>,
  maxGap: number = WORKSPACE_GRAPH_ROW_GAP
): void {
  const parentByChild = buildLayoutParentMap(childrenByParent);

  const findLaneRoot = (nodeId: string): string => {
    let current = nodeId;
    for (let guard = 0; guard < 48; guard += 1) {
      const parentId = parentByChild.get(current);
      if (!parentId) return current;
      const parent = nodeById.get(parentId);
      if (!parent) return current;
      if (parent.type === "account" || parent.type === "strategy") return current;
      current = parentId;
    }
    return nodeId;
  };

  const laneRootIds = new Set<string>();
  for (const node of nodes) {
    if (!positions.has(node.id)) continue;
    if (node.type === "account") continue;
    laneRootIds.add(findLaneRoot(node.id));
  }

  for (let pass = 0; pass < 8; pass += 1) {
    const byColumn = new Map<
      number,
      Array<{ id: string; top: number; bottom: number }>
    >();
    for (const node of nodes) {
      const pos = positions.get(node.id);
      if (!pos) continue;
      const { height } = getWorkspaceNodeDimensions(node);
      const key = layoutColumnKey(pos.x);
      const list = byColumn.get(key) ?? [];
      list.push({ id: node.id, top: pos.y, bottom: pos.y + height });
      byColumn.set(key, list);
    }

    let moved = false;
    Array.from(byColumn.values()).forEach((items) => {
      const ordered = [...items].sort((a, b) => {
        if (a.top !== b.top) return a.top - b.top;
        return a.id.localeCompare(b.id);
      });
      for (let index = 1; index < ordered.length; index += 1) {
        const upper = ordered[index - 1]!;
        const lower = ordered[index]!;
        const excess = lower.top - upper.bottom - maxGap;
        if (excess <= WORKSPACE_NODE_SNAP_GRID) continue;

        const lowerRoot = findLaneRoot(lower.id);
        const upperRoot = findLaneRoot(upper.id);

        // Same lane: only pack when cards belong to different direct branches
        // under that lane root (e.g. two sims under one report).
        let pullRoot = lowerRoot;
        let clearanceUppers: string[] = [];

        if (lowerRoot === upperRoot) {
          const findBranch = (nodeId: string): string | null => {
            let current = nodeId;
            for (let guard = 0; guard < 48; guard += 1) {
              const parentId = parentByChild.get(current);
              if (!parentId) return null;
              if (parentId === lowerRoot) return current;
              current = parentId;
            }
            return null;
          };
          const lowerBranch = findBranch(lower.id);
          const upperBranch = findBranch(upper.id);
          if (!lowerBranch || !upperBranch || lowerBranch === upperBranch) continue;
          pullRoot = lowerBranch;
          clearanceUppers = (childrenByParent.get(lowerRoot) ?? []).filter(
            (id) => id !== lowerBranch
          );
        } else {
          const upperRootNode = nodeById.get(upperRoot);
          const lowerRootNode = nodeById.get(lowerRoot);
          // Do not pull one report's sims/policies up beside another report's stack.
          if (upperRootNode?.type === "report" && lowerRootNode?.type === "report") {
            continue;
          }
          const lowerCols = measureSubtreeColumnExtents(
            lowerRoot,
            positions,
            childrenByParent,
            nodeById
          );
          let lowerTop = Infinity;
          Array.from(lowerCols.values()).forEach((ext) => {
            lowerTop = Math.min(lowerTop, ext.top);
          });
          if (!Number.isFinite(lowerTop)) continue;

          clearanceUppers = Array.from(laneRootIds).filter((id) => {
            if (id === lowerRoot) return false;
            const cols = measureSubtreeColumnExtents(
              id,
              positions,
              childrenByParent,
              nodeById
            );
            let top = Infinity;
            Array.from(cols.values()).forEach((ext) => {
              top = Math.min(top, ext.top);
            });
            return Number.isFinite(top) && top <= lowerTop + 0.5;
          });
        }

        const clearance = columnAwareClearanceAgainstUppers(
          pullRoot,
          clearanceUppers,
          maxGap,
          positions,
          childrenByParent,
          nodeById
        );
        // clearance < 0 ⇒ room to pull up; take the smaller move so we do not
        // overshoot another column’s obstacle.
        if (clearance >= -0.5) continue;
        const dy = Math.max(clearance, -excess);
        if (dy >= -0.5) continue;

        shiftLayoutSubtreeFrom(pullRoot, dy, childrenByParent, positions);
        moved = true;
      }
    });
    if (!moved) break;
  }
}

/**
 * @deprecated Prefer {@link packCrossLaneColumnGaps}. Kept for any residual
 * call sites expecting global band collapse.
 */
function collapseExcessVerticalGaps(
  nodes: WorkspaceNode[],
  positions: Map<string, WorkspaceNodePosition>,
  maxGap: number = WORKSPACE_GRAPH_ROW_GAP
): void {
  const nodeById = new Map(nodes.map((node) => [node.id, node]));

  const items = nodes
    .filter((node) => positions.has(node.id))
    .map((node) => {
      const pos = positions.get(node.id)!;
      const { height } = getWorkspaceNodeDimensions(node);
      return { id: node.id, top: pos.y, bottom: pos.y + height };
    })
    .sort((a, b) => {
      if (a.top !== b.top) return a.top - b.top;
      return a.id.localeCompare(b.id);
    });

  if (items.length === 0) return;

  const bands: Array<{ top: number; bottom: number; nodeIds: string[] }> = [];
  for (const item of items) {
    const last = bands[bands.length - 1];
    // Merge overlapping / touching cards only — row-gapped stacks stay separate
    // so we can detect the empty corridor between lanes.
    if (!last || item.top > last.bottom + 0.5) {
      bands.push({ top: item.top, bottom: item.bottom, nodeIds: [item.id] });
    } else {
      last.bottom = Math.max(last.bottom, item.bottom);
      last.nodeIds.push(item.id);
    }
  }

  let prevBottom = -Infinity;
  for (const band of bands) {
    let currentTop = Infinity;
    for (const id of band.nodeIds) {
      const pos = positions.get(id);
      if (pos) currentTop = Math.min(currentTop, pos.y);
    }
    if (!Number.isFinite(currentTop)) continue;

    if (Number.isFinite(prevBottom)) {
      const gap = currentTop - prevBottom;
      const excess = gap - maxGap;
      // Ignore sub-card jitter from snap; only pull up real empty corridors.
      if (excess > WORKSPACE_NODE_SNAP_GRID) {
        const dy = -excess;
        for (const id of band.nodeIds) {
          const pos = positions.get(id);
          if (!pos) continue;
          positions.set(id, { x: pos.x, y: pos.y + dy });
        }
      }
    }

    let currentBottom = -Infinity;
    for (const id of band.nodeIds) {
      const node = nodeById.get(id);
      const pos = positions.get(id);
      if (!node || !pos) continue;
      const { height } = getWorkspaceNodeDimensions(node);
      currentBottom = Math.max(currentBottom, pos.y + height);
    }
    if (!Number.isFinite(currentBottom)) continue;
    prevBottom = currentBottom;
  }
}

function shouldResolveSiblingLanes(parent: WorkspaceNode): boolean {
  // Account→strategy/report branches on account canvases.
  // Federated Strategy→Account lanes are compacted by packStrategyAccountLanes.
  return parent.type === "account";
}

/**
 * Within account/strategy sibling groups, resolve vertical overlap by shifting
 * whole branch lanes bidirectionally (upper lane up, lower lane down).
 */
function resolveSiblingLaneOverlaps(
  nodes: WorkspaceNode[],
  positions: Map<string, WorkspaceNodePosition>,
  childrenByParent: Map<string, string[]>,
  edges: WorkspaceEdge[],
  nodeById: Map<string, WorkspaceNode>,
  options?: { viewMode?: "account" | "strategy"; compact?: boolean }
): void {
  const viewMode = options?.viewMode ?? "account";
  const compact = options?.compact ?? false;
  const parents = nodes
    .filter((node) => (childrenByParent.get(node.id)?.length ?? 0) > 1)
    .sort((a, b) => {
      const aPos = positions.get(a.id);
      const bPos = positions.get(b.id);
      if (!aPos || !bPos) return 0;
      if (aPos.x !== bPos.x) return aPos.x - bPos.x;
      return aPos.y - bPos.y;
    });

  let changed = true;
  let guard = 0;
  while (changed && guard < 12) {
    guard += 1;
    changed = false;

    for (const parent of parents) {
      const childIds = childrenByParent.get(parent.id) ?? [];
      if (childIds.length <= 1) continue;

      const children = childIds
        .map((id) => nodeById.get(id))
        .filter((node): node is WorkspaceNode => Boolean(node));

      if (!shouldResolveSiblingLanes(parent)) continue;

      const gap = gapBetweenSiblings(parent, viewMode, compact);
      const sorted = [...children].sort((a, b) => {
        const aExtent = measureLaneExtent(
          a.id,
          nodes,
          positions,
          edges,
          nodeById,
          childrenByParent
        );
        const bExtent = measureLaneExtent(
          b.id,
          nodes,
          positions,
          edges,
          nodeById,
          childrenByParent
        );
        const aTop = aExtent?.top ?? positions.get(a.id)?.y ?? 0;
        const bTop = bExtent?.top ?? positions.get(b.id)?.y ?? 0;
        if (aTop !== bTop) return aTop - bTop;
        return a.id.localeCompare(b.id);
      });

      for (let index = 1; index < sorted.length; index += 1) {
        const upper = sorted[index - 1]!;
        const lower = sorted[index]!;
        const upperIds = sorted.slice(0, index).map((node) => node.id);
        const overlap = columnAwareClearanceAgainstUppers(
          lower.id,
          upperIds,
          gap,
          positions,
          childrenByParent,
          nodeById
        );
        if (overlap <= 0.5) continue;

        const upperExtent = measureLaneExtent(
          upper.id,
          nodes,
          positions,
          edges,
          nodeById,
          childrenByParent
        );
        let roomAbove = (upperExtent?.top ?? PIPELINE_TOP_Y) - PIPELINE_TOP_Y;
        if (index > 1) {
          const prevIds = sorted.slice(0, index - 1).map((node) => node.id);
          const prevClearance = columnAwareClearanceAgainstUppers(
            upper.id,
            prevIds,
            gap,
            positions,
            childrenByParent,
            nodeById
          );
          // prevClearance <= 0 means upper already clears priors; slack above is -prevClearance.
          if (prevClearance < 0) {
            roomAbove = Math.min(roomAbove, -prevClearance);
          } else {
            roomAbove = 0;
          }
        }
        roomAbove = Math.max(0, roomAbove);

        const spreadUp = Math.min(overlap / 2, roomAbove);
        const spreadDown = overlap - spreadUp;

        if (spreadUp > 0.5) {
          shiftLayoutSubtreeFrom(upper.id, -spreadUp, childrenByParent, positions);
          changed = true;
        }
        if (spreadDown > 0.5) {
          shiftLayoutSubtreeFrom(lower.id, spreadDown, childrenByParent, positions);
          changed = true;
        }
      }
    }
  }
}

const SAME_COLUMN_OVERLAP_TYPES = new Set<WorkspaceNodeType>([
  "report",
  "simulation",
  "policy",
  "combined_policy",
  "combined_simulation",
]);

function buildLayoutParentMap(
  childrenByParent: Map<string, string[]>
): Map<string, string> {
  const parentByChild = new Map<string, string>();
  Array.from(childrenByParent.entries()).forEach(([parentId, childIds]) => {
    for (const childId of childIds) {
      if (!parentByChild.has(childId)) parentByChild.set(childId, parentId);
    }
  });
  return parentByChild;
}

function isSameColumnStackRoot(
  node: WorkspaceNode,
  parentByChild: Map<string, string>,
  nodeById: Map<string, WorkspaceNode>
): boolean {
  const parentId = parentByChild.get(node.id);
  if (!parentId) return true;
  const parent = nodeById.get(parentId);
  if (!parent) return true;
  return parent.type !== node.type;
}

function layoutSameColumnStackBottom(
  node: WorkspaceNode,
  positions: Map<string, WorkspaceNodePosition>,
  childrenByParent: Map<string, string[]>,
  nodeById: Map<string, WorkspaceNode>,
  edges: WorkspaceEdge[]
): number {
  const pos = positions.get(node.id);
  if (!pos) return 0;
  const { height } = getWorkspaceNodeDimensions(node);
  let bottom = pos.y + height;

  for (const childId of childrenByParent.get(node.id) ?? []) {
    const child = nodeById.get(childId);
    if (!child || child.type !== node.type) continue;
    bottom = Math.max(
      bottom,
      layoutSameColumnStackBottom(child, positions, childrenByParent, nodeById, edges)
    );
  }

  return bottomWithPlaceholderReserve(node, bottom, edges, nodeById, positions);
}

function findLowestCommonAncestor(
  a: string,
  b: string,
  parentByChild: Map<string, string>
): string | null {
  const ancestorsB = new Set<string>();
  let cursor: string | undefined = b;
  while (cursor) {
    ancestorsB.add(cursor);
    cursor = parentByChild.get(cursor);
  }
  cursor = a;
  while (cursor) {
    if (ancestorsB.has(cursor)) return cursor;
    cursor = parentByChild.get(cursor);
  }
  return null;
}

function findBranchShiftRoot(
  stackRootId: string,
  otherStackRootId: string,
  parentByChild: Map<string, string>
): string {
  const lca = findLowestCommonAncestor(stackRootId, otherStackRootId, parentByChild);
  if (!lca) return stackRootId;

  let cursor = stackRootId;
  while (true) {
    const parentId = parentByChild.get(cursor);
    if (!parentId || parentId === lca) return cursor;
    cursor = parentId;
  }
}

function simulationLaneReportRoot(
  stackRootId: string,
  parentByChild: Map<string, string>,
  nodeById: Map<string, WorkspaceNode>
): string {
  let cursor = stackRootId;
  while (true) {
    const node = nodeById.get(cursor);
    if (!node || node.type === "report") return cursor;
    const parentId = parentByChild.get(cursor);
    if (!parentId) return cursor;
    cursor = parentId;
  }
}

function columnOverlapBranchAnchor(
  stackRoot: WorkspaceNode,
  otherStackRoot: WorkspaceNode,
  parentByChild: Map<string, string>,
  nodeById: Map<string, WorkspaceNode>
): string {
  if (stackRoot.type === "simulation") {
    const thisLane = simulationLaneReportRoot(stackRoot.id, parentByChild, nodeById);
    const otherLane = simulationLaneReportRoot(otherStackRoot.id, parentByChild, nodeById);
    const thisLaneNode = nodeById.get(thisLane);
    // Distinct report lanes: shift the whole report so report/sim rows stay aligned.
    // Shared report (parallel sims) or no report: shift the sim branch under the LCA —
    // moving the shared report would move both stacks together and leave reruns overlapping.
    if (thisLane !== otherLane && thisLaneNode?.type === "report") {
      return thisLane;
    }
    return findBranchShiftRoot(stackRoot.id, otherStackRoot.id, parentByChild);
  }
  return findBranchShiftRoot(stackRoot.id, otherStackRoot.id, parentByChild);
}

/** When two stacks in the same column overlap, split the gap: upper up, lower down. */
function spreadSameColumnStackOverlap(
  upper: WorkspaceNode,
  lower: WorkspaceNode,
  overlap: number,
  roomAbove: number,
  parentByChild: Map<string, string>,
  childrenByParent: Map<string, string[]>,
  positions: Map<string, WorkspaceNodePosition>,
  nodeById: Map<string, WorkspaceNode>
): boolean {
  if (overlap <= 0.5) return false;
  const spreadUp = Math.min(overlap / 2, Math.max(0, roomAbove));
  const spreadDown = overlap - spreadUp;
  let changed = false;
  const upperAnchor = columnOverlapBranchAnchor(upper, lower, parentByChild, nodeById);
  const lowerAnchor = columnOverlapBranchAnchor(lower, upper, parentByChild, nodeById);
  // Shared ancestor cannot resolve relative overlap — push the lower stack only.
  if (upperAnchor === lowerAnchor) {
    shiftLayoutSubtreeFrom(lower.id, overlap, childrenByParent, positions);
    return overlap > 0.5;
  }
  if (spreadUp > 0.5) {
    shiftLayoutSubtreeFrom(upperAnchor, -spreadUp, childrenByParent, positions);
    changed = true;
  }
  if (spreadDown > 0.5) {
    shiftLayoutSubtreeFrom(lowerAnchor, spreadDown, childrenByParent, positions);
    changed = true;
  }
  return changed;
}

/** Residual same-column overlaps (combined policies, parallel sim stacks, reruns). */
function separateSameColumnOverlaps(
  nodes: WorkspaceNode[],
  positions: Map<string, WorkspaceNodePosition>,
  childrenByParent: Map<string, string[]>,
  edges: WorkspaceEdge[],
  nodeById: Map<string, WorkspaceNode>
): void {
  const parentByChild = buildLayoutParentMap(childrenByParent);

  const byType = new Map<WorkspaceNodeType, WorkspaceNode[]>();
  for (const node of nodes) {
    if (!positions.has(node.id)) continue;
    if (!SAME_COLUMN_OVERLAP_TYPES.has(node.type)) continue;
    if (!isSameColumnStackRoot(node, parentByChild, nodeById)) continue;
    const list = byType.get(node.type) ?? [];
    list.push(node);
    byType.set(node.type, list);
  }

  let changed = true;
  let guard = 0;
  while (changed && guard < 12) {
    guard += 1;
    changed = false;

    Array.from(byType.values()).forEach((group) => {
      const ordered = [...group].sort((a, b) => {
        const aPos = positions.get(a.id)!;
        const bPos = positions.get(b.id)!;
        if (aPos.y !== bPos.y) return aPos.y - bPos.y;
        return a.id.localeCompare(b.id);
      });

      for (let index = 1; index < ordered.length; index += 1) {
        const upper = ordered[index - 1]!;
        const lower = ordered[index]!;
        const upperBottom = layoutSameColumnStackBottom(
          upper,
          positions,
          childrenByParent,
          nodeById,
          edges
        );
        const lowerTop = positions.get(lower.id)?.y ?? 0;
        const overlap = upperBottom + WORKSPACE_GRAPH_ROW_GAP - lowerTop;
        if (overlap <= 0.5) continue;

        let roomAbove = (positions.get(upper.id)?.y ?? 0) - PIPELINE_TOP_Y;
        if (index > 1) {
          const prev = ordered[index - 2]!;
          const prevBottom = layoutSameColumnStackBottom(
            prev,
            positions,
            childrenByParent,
            nodeById,
            edges
          );
          roomAbove = Math.min(
            roomAbove,
            (positions.get(upper.id)?.y ?? 0) - (prevBottom + WORKSPACE_GRAPH_ROW_GAP)
          );
        }
        roomAbove = Math.max(0, roomAbove);

        if (
          spreadSameColumnStackOverlap(
            upper,
            lower,
            overlap,
            roomAbove,
            parentByChild,
            childrenByParent,
            positions,
            nodeById
          )
        ) {
          changed = true;
        }
      }
    });
  }
}

/** Final safety net — push any residual colliding card down until the graph is clear. */
function enforceNoNodeOverlaps(
  nodes: WorkspaceNode[],
  positions: Map<string, WorkspaceNodePosition>,
  options?: {
    viewMode?: "account" | "strategy";
    childrenByParent?: Map<string, string[]>;
    nodeById?: Map<string, WorkspaceNode>;
    parentByChild?: Map<string, string>;
  }
): void {
  const gap = WORKSPACE_NODE_COLLISION_GAP;
  const viewMode = options?.viewMode ?? "account";
  const childrenByParent = options?.childrenByParent;
  const nodeById = options?.nodeById;
  const parentByChild = options?.parentByChild;
  let changed = true;
  let guard = 0;
  while (changed && guard < 48) {
    guard += 1;
    changed = false;

    const ordered = nodes
      .filter((node) => positions.has(node.id))
      .sort((a, b) => {
        const aPos = positions.get(a.id)!;
        const bPos = positions.get(b.id)!;
        if (aPos.y !== bPos.y) return aPos.y - bPos.y;
        if (aPos.x !== bPos.x) return aPos.x - bPos.x;
        return a.id.localeCompare(b.id);
      });

    for (let i = 0; i < ordered.length; i += 1) {
      const upper = ordered[i]!;
      const upperPos = positions.get(upper.id)!;
      const upperSize = getWorkspaceNodeDimensions(upper);

      for (let j = i + 1; j < ordered.length; j += 1) {
        const lower = ordered[j]!;
        const lowerPos = positions.get(lower.id)!;
        const lowerSize = getWorkspaceNodeDimensions(lower);

        const overlaps =
          upperPos.x < lowerPos.x + lowerSize.width + gap &&
          upperPos.x + upperSize.width + gap > lowerPos.x &&
          upperPos.y < lowerPos.y + lowerSize.height + gap &&
          upperPos.y + upperSize.height + gap > lowerPos.y;
        if (!overlaps) continue;

        const needY = upperPos.y + upperSize.height + gap - lowerPos.y;
        if (needY <= 0.5) continue;

        // Strategy federated: move the whole account lane so reports stay with their account.
        if (
          viewMode === "strategy" &&
          childrenByParent &&
          nodeById &&
          parentByChild
        ) {
          const lowerAnchor = accountLaneAnchorId(lower.id, parentByChild, nodeById);
          const upperAnchor = accountLaneAnchorId(upper.id, parentByChild, nodeById);
          if (lowerAnchor && upperAnchor && lowerAnchor !== upperAnchor) {
            shiftLayoutSubtreeFrom(lowerAnchor, needY, childrenByParent, positions);
            changed = true;
            continue;
          }
        }

        positions.set(lower.id, { x: lowerPos.x, y: lowerPos.y + needY });
        changed = true;
      }
    }
  }
}

/** Direct child of an account when walking up from `nodeId` (strategy or report branch root). */
function accountChildBranchRootId(
  nodeId: string,
  parentByChild: Map<string, string>,
  nodeById: Map<string, WorkspaceNode>
): string | null {
  let cursor: string | undefined = nodeId;
  while (cursor) {
    const parentId = parentByChild.get(cursor);
    if (!parentId) return null;
    const parent = nodeById.get(parentId);
    if (parent?.type === "account") return cursor;
    cursor = parentId;
  }
  return null;
}

/** True when multi-parent sources sit under two or more distinct account child branches. */
function multiParentSpansDistinctAccountChildBranches(
  parentIds: string[],
  parentByChild: Map<string, string>,
  nodeById: Map<string, WorkspaceNode>
): boolean {
  const branches = new Set<string>();
  for (const parentId of parentIds) {
    const branchId = accountChildBranchRootId(parentId, parentByChild, nodeById);
    if (branchId) branches.add(branchId);
  }
  return branches.size > 1;
}

/** Nearest account ancestor (or self) — used to keep federated account lanes intact. */
function accountLaneAnchorId(
  nodeId: string,
  parentByChild: Map<string, string>,
  nodeById: Map<string, WorkspaceNode>
): string | null {
  let cursor: string | undefined = nodeId;
  while (cursor) {
    const node = nodeById.get(cursor);
    if (node?.type === "account") return cursor;
    cursor = parentByChild.get(cursor);
  }
  return null;
}

/** True when multi-parent sources sit under two or more distinct account lanes. */
function multiParentSpansDistinctAccountLanes(
  parentIds: string[],
  parentByChild: Map<string, string>,
  nodeById: Map<string, WorkspaceNode>
): boolean {
  const accounts = new Set<string>();
  for (const parentId of parentIds) {
    const accountId = accountLaneAnchorId(parentId, parentByChild, nodeById);
    if (accountId) accounts.add(accountId);
  }
  return accounts.size > 1;
}

/**
 * Pull sibling lanes together when the gap between them exceeds the intended
 * branch/row padding (collapse empty vertical corridors with no cards).
 */
/**
 * Dy to pull `lowerReportId` so it clears the upper report's full sim/policy
 * lineage. Positive = still overlapping; negative = room to pull up.
 */
function reportLineageClearanceDy(
  upperReportId: string,
  lowerReportId: string,
  positions: Map<string, WorkspaceNodePosition>,
  childrenByParent: Map<string, string[]>,
  nodeById: Map<string, WorkspaceNode>,
  edges: WorkspaceEdge[],
  allNodes: WorkspaceNode[],
  compact = false
): number {
  const upperExtent = measureLaneExtent(
    upperReportId,
    allNodes,
    positions,
    edges,
    nodeById,
    childrenByParent
  );
  const lowerExtent = measureLaneExtent(
    lowerReportId,
    allNodes,
    positions,
    edges,
    nodeById,
    childrenByParent
  );
  if (!upperExtent || !lowerExtent) return 0;
  const gap = gapAfterReportLineage(upperReportId, childrenByParent, compact);
  return upperExtent.bottom + gap - lowerExtent.top;
}

function packSiblingLanes(
  nodes: WorkspaceNode[],
  positions: Map<string, WorkspaceNodePosition>,
  childrenByParent: Map<string, string[]>,
  edges: WorkspaceEdge[],
  nodeById: Map<string, WorkspaceNode>,
  options?: { viewMode?: "account" | "strategy"; compact?: boolean }
): void {
  const viewMode = options?.viewMode ?? "account";
  const compact = options?.compact ?? false;

  const parents = nodes.filter((node) => {
    if ((childrenByParent.get(node.id)?.length ?? 0) <= 1) return false;
    // Pack account→strategy / account→report and strategy→report sibling branches.
    return node.type === "account" || node.type === "strategy";
  });

  for (const parent of parents) {
    const childIds = childrenByParent.get(parent.id) ?? [];
    const children = childIds
      .map((id) => nodeById.get(id))
      .filter((node): node is WorkspaceNode => Boolean(node));
    if (children.length <= 1) continue;

    const gap = gapBetweenSiblings(parent, viewMode, compact);
    const sorted = [...children].sort((a, b) => {
      const aExtent = measureLaneExtent(
        a.id,
        nodes,
        positions,
        edges,
        nodeById,
        childrenByParent
      );
      const bExtent = measureLaneExtent(
        b.id,
        nodes,
        positions,
        edges,
        nodeById,
        childrenByParent
      );
      const aTop = aExtent?.top ?? positions.get(a.id)?.y ?? 0;
      const bTop = bExtent?.top ?? positions.get(b.id)?.y ?? 0;
      if (aTop !== bTop) return aTop - bTop;
      return a.id.localeCompare(b.id);
    });

    const useReportLineage =
      parent.type === "strategy" || parent.type === "account";

    for (let index = 1; index < sorted.length; index += 1) {
      const lower = sorted[index]!;
      const upperIds = sorted.slice(0, index).map((node) => node.id);
      let dy: number;

      if (useReportLineage && lower.type === "report") {
        const upperReports = upperIds.filter(
          (id) => nodeById.get(id)?.type === "report"
        );
        const upperOther = upperIds.filter(
          (id) => nodeById.get(id)?.type !== "report"
        );
        dy = -Infinity;
        for (const upperId of upperReports) {
          dy = Math.max(
            dy,
            reportLineageClearanceDy(
              upperId,
              lower.id,
              positions,
              childrenByParent,
              nodeById,
              edges,
              nodes,
              compact
            )
          );
        }
        if (upperOther.length > 0) {
          dy = Math.max(
            dy,
            columnAwareClearanceAgainstUppers(
              lower.id,
              upperOther,
              gap,
              positions,
              childrenByParent,
              nodeById
            )
          );
        }
        if (!Number.isFinite(dy)) dy = 0;
        // Enforce minimum lineage gap (push down) and collapse excess (pull up).
        if (Math.abs(dy) > 0.5) {
          shiftLayoutSubtreeFrom(lower.id, dy, childrenByParent, positions);
        }
        continue;
      }

      dy = columnAwareClearanceAgainstUppers(
        lower.id,
        upperIds,
        gap,
        positions,
        childrenByParent,
        nodeById
      );
      // Only collapse excess empty space (pull up). Overlaps are handled earlier.
      if (dy < -0.5) {
        shiftLayoutSubtreeFrom(lower.id, dy, childrenByParent, positions);
      }
    }

    if (parent.type === "account") {
      recenterAccountOnReportChildren(parent, positions, childrenByParent, nodeById);
    }
  }
}

/**
 * Re-stack Strategy → Account lanes so each account’s reports/sims stay in one
 * contiguous vertical block (no cross-account interleaving after overlap nudges).
 */
function packStrategyAccountLanes(
  nodes: WorkspaceNode[],
  positions: Map<string, WorkspaceNodePosition>,
  childrenByParent: Map<string, string[]>,
  edges: WorkspaceEdge[],
  nodeById: Map<string, WorkspaceNode>,
  options?: { compact?: boolean }
): void {
  const branchGap = layoutBranchGap(options?.compact ?? false);
  const strategies = nodes.filter((node) => node.type === "strategy");
  for (const strategy of strategies) {
    const accounts = childNodesFor(strategy.id, childrenByParent, nodeById).filter(
      (node) => node.type === "account"
    );
    if (accounts.length === 0) continue;

    const ordered = [...accounts].sort((a, b) => {
      const aExtent = measureLaneExtent(
        a.id,
        nodes,
        positions,
        edges,
        nodeById,
        childrenByParent
      );
      const bExtent = measureLaneExtent(
        b.id,
        nodes,
        positions,
        edges,
        nodeById,
        childrenByParent
      );
      const aTop = aExtent?.top ?? positions.get(a.id)?.y ?? 0;
      const bTop = bExtent?.top ?? positions.get(b.id)?.y ?? 0;
      if (aTop !== bTop) return aTop - bTop;
      return a.id.localeCompare(b.id);
    });

    let yCursor = PIPELINE_TOP_Y;
    for (const account of ordered) {
      const extent = measureLaneExtent(
        account.id,
        nodes,
        positions,
        edges,
        nodeById,
        childrenByParent
      );
      if (!extent) continue;
      const dy = yCursor - extent.top;
      if (Math.abs(dy) > 0.5) {
        shiftLayoutSubtreeFrom(account.id, dy, childrenByParent, positions);
      }
      recenterAccountOnReportChildren(account, positions, childrenByParent, nodeById);
      const packed = measureLaneExtent(
        account.id,
        nodes,
        positions,
        edges,
        nodeById,
        childrenByParent
      );
      yCursor = (packed?.bottom ?? extent.bottom + dy) + branchGap;
    }

    // Keep the strategy root on the first account lane spine (horizontal primary
    // edge). Centering between lanes opened long vertical wires through empty space.
    const firstAccount = ordered[0];
    if (firstAccount) {
      const accountPos = positions.get(firstAccount.id);
      const strategyPos = positions.get(strategy.id);
      if (accountPos && strategyPos) {
        const { width, height } = getWorkspaceNodeDimensions(strategy);
        const { height: accountHeight } = getWorkspaceNodeDimensions(firstAccount);
        const targetCenter = accountPos.y + accountHeight / 2;
        positions.set(
          strategy.id,
          snapWorkspaceNodeByCenter(
            { x: strategyPos.x, y: targetCenter - height / 2 },
            { width, height },
            WORKSPACE_NODE_SNAP_GRID
          )
        );
      }
    }
  }
}

function recenterAccountOnReportChildren(
  account: WorkspaceNode,
  positions: Map<string, WorkspaceNodePosition>,
  childrenByParent: Map<string, string[]>,
  nodeById: Map<string, WorkspaceNode>
): void {
  const reports = (childrenByParent.get(account.id) ?? [])
    .map((id) => nodeById.get(id))
    .filter((node): node is WorkspaceNode => Boolean(node && node.type === "report"));
  if (reports.length === 0) return;

  let sumCenter = 0;
  let count = 0;
  for (const report of reports) {
    const pos = positions.get(report.id);
    if (!pos) continue;
    const { height } = getWorkspaceNodeDimensions(report);
    sumCenter += pos.y + height / 2;
    count += 1;
  }
  if (count === 0) return;

  const accountPos = positions.get(account.id);
  if (!accountPos) return;
  const { width, height } = getWorkspaceNodeDimensions(account);
  positions.set(
    account.id,
    snapWorkspaceNodeByCenter(
      { x: accountPos.x, y: sumCenter / count - height / 2 },
      { width, height },
      WORKSPACE_NODE_SNAP_GRID
    )
  );
}

function shiftLayoutSubtreeFrom(
  rootId: string,
  dy: number,
  childrenByParent: Map<string, string[]>,
  positions: Map<string, WorkspaceNodePosition>
): void {
  if (Math.abs(dy) < 0.5) return;
  const pos = positions.get(rootId);
  if (pos) {
    positions.set(rootId, { x: pos.x, y: pos.y + dy });
  }
  shiftLayoutDescendants(rootId, dy, childrenByParent, positions);
}

/** Move every descendant in the layout tree by dy (root itself is not moved). */
function shiftLayoutDescendants(
  rootId: string,
  dy: number,
  childrenByParent: Map<string, string[]>,
  positions: Map<string, WorkspaceNodePosition>
): void {
  const stack = [...(childrenByParent.get(rootId) ?? [])];
  const seen = new Set<string>([rootId]);
  while (stack.length > 0) {
    const id = stack.pop()!;
    if (seen.has(id)) continue;
    seen.add(id);
    const pos = positions.get(id);
    if (pos) {
      positions.set(id, { x: pos.x, y: pos.y + dy });
    }
    for (const childId of childrenByParent.get(id) ?? []) {
      stack.push(childId);
    }
  }
}

/** Auto-layout entry point (column-based LR pipeline). Parallel Strategy packing only. */
export function layoutWorkspaceGraphWithDagre(
  nodes: WorkspaceNode[],
  edges: WorkspaceEdge[],
  options?: { viewMode?: "account" | "strategy"; compact?: boolean }
): Map<string, WorkspaceNodePosition> {
  return layoutWorkspaceGraphTree(nodes, edges, options);
}

export function workspaceGraphPositionsToBulkPayload(
  positions: Map<string, WorkspaceNodePosition>,
  nodes: WorkspaceNode[] = []
): Array<{ nodeId: string; x: number; y: number }> {
  const byId = new Map(nodes.map((node) => [node.id, node]));
  return Array.from(positions.entries())
    .filter(([nodeId]) => {
      const node = byId.get(nodeId);
      if (!node) return true;
      return !shouldSkipWorkspacePositionPersist(node);
    })
    .map(([nodeId, position]) => {
      const node = byId.get(nodeId);
      return {
        nodeId: node ? workspacePersistNodeId(node) : nodeId,
        x: position.x,
        y: position.y,
      };
    });
}
